/**
 * Projects table, the list layout of the projects listing page.
 *
 * @category components
 */

import {
  AnimationEvent,
  MouseEvent,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { t } from "@lingui/core/macro";

import { Material, useMaterials } from "_/data/materials";
import { Attempt, Project, sortAttempts } from "_/data/projects";
import { User, useUsers } from "_/data/users";
import { projectUrls } from "_/routes";
import { indexById } from "_/utils";

import { UserAvatar } from "_/components/avatar";
import { RelativeDateTime } from "_/components/date-time";
import { MaterialsList } from "_/components/materials";
import {
  ArchivedProjectsHeader,
  useProjectsListing,
} from "_/components/project-card";
import { ProjectThumbnail } from "_/components/project-thumbnail";
import { useLocation } from "_/components/router";
import { ColumnDef, CustomCellLink, Table } from "_/components/table";

import * as S from "./styled";

type ProjectRow = {
  project: Project;
  owner: User | undefined;
  lastAttempt: Attempt | undefined;
  materials: Material[];
};

const THUMBNAIL_SIZE_REM = 3;
const ENLARGED_THUMBNAIL_SIZE_REM = 12;
const THUMBNAIL_SIZE = `${THUMBNAIL_SIZE_REM}rem`;
const ENLARGED_THUMBNAIL_SIZE = `${ENLARGED_THUMBNAIL_SIZE_REM}rem`;
const THUMBNAIL_SCALE = THUMBNAIL_SIZE_REM / ENLARGED_THUMBNAIL_SIZE_REM;
const ENLARGE_HOVER_DELAY_MS = 500;

/**
 * Keep `anchorRect` matching the thumbnail's position on screen while the
 * enlarged copy is shown, since the copy uses fixed viewport coordinates.
 */
function useFollowThumbnailPosition(
  thumbnailRef: RefObject<HTMLDivElement | null>,
  isEnlarged: boolean,
  setAnchorRect: (rect: DOMRect) => void
) {
  useEffect(() => {
    if (!isEnlarged) {
      return undefined;
    }

    const followThumbnail = () => {
      const thumbnail = thumbnailRef.current;

      if (thumbnail) {
        setAnchorRect(thumbnail.getBoundingClientRect());
      }
    };

    // Scroll events don't bubble, so listen in the capture phase to also
    // catch scrolling of the table or any other container around it.
    const scrollOptions = { capture: true, passive: true };
    window.addEventListener("scroll", followThumbnail, scrollOptions);
    window.addEventListener("resize", followThumbnail);

    return () => {
      window.removeEventListener("scroll", followThumbnail, scrollOptions);
      window.removeEventListener("resize", followThumbnail);
    };
  }, [thumbnailRef, isEnlarged, setAnchorRect]);
}

/**
 * Table thumbnail that shows an enlarged copy after being hovered for
 * `ENLARGE_HOVER_DELAY_MS`. When the pointer leaves, the copy shrinks back
 * into the thumbnail and is removed once that animation finishes.
 */
function EnlargeableThumbnail({ project }: { project: Project }) {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [isShrinking, setIsShrinking] = useState(false);
  const thumbnailRef = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(hoverTimer.current), []);
  useFollowThumbnailPosition(thumbnailRef, anchorRect !== null, setAnchorRect);

  const startHover = (event: MouseEvent<HTMLDivElement>) => {
    const thumbnail = event.currentTarget;
    window.clearTimeout(hoverTimer.current);
    hoverTimer.current = window.setTimeout(() => {
      setAnchorRect(thumbnail.getBoundingClientRect());
      setIsShrinking(false);
    }, ENLARGE_HOVER_DELAY_MS);
  };

  const endHover = () => {
    window.clearTimeout(hoverTimer.current);

    if (anchorRect) {
      setIsShrinking(true);
    }
  };

  const removeAfterShrinking = (event: AnimationEvent<HTMLDivElement>) => {
    // Ignore animations bubbling up from inside the copy, like the
    // placeholder's pulse.
    if (event.target !== event.currentTarget || !isShrinking) {
      return;
    }

    setAnchorRect(null);
    setIsShrinking(false);
  };

  const enlargedPosition = anchorRect && {
    left: anchorRect.left,
    top: anchorRect.top + anchorRect.height / 2,
  };

  const enlarged = enlargedPosition && (
    <S.EnlargedThumbnail
      style={enlargedPosition}
      aria-hidden
      onAnimationEnd={removeAfterShrinking}
      $thumbnailScale={THUMBNAIL_SCALE}
      $isShrinking={isShrinking}
    >
      <ProjectThumbnail project={project} size={ENLARGED_THUMBNAIL_SIZE} />
    </S.EnlargedThumbnail>
  );

  return (
    <div ref={thumbnailRef} onMouseEnter={startHover} onMouseLeave={endHover}>
      <ProjectThumbnail project={project} size={THUMBNAIL_SIZE} />
      {enlarged && createPortal(enlarged, document.body)}
    </div>
  );
}

/**
 * Every material used by any revision of the project, de-duplicated, with the
 * materials of the most recent revision first.
 */
function projectMaterials(
  project: Project,
  materialsById: Record<string, Material>
): Material[] {
  const newestFirst = project.revisions.toSorted((a, b) => b.number - a.number);
  const materialIds = newestFirst
    .flatMap((revision) => revision.materials)
    .filter((id) => id != null);
  const uniqueIds = [...new Set(materialIds)];

  return uniqueIds.flatMap((id) => materialsById[id] ?? []);
}

const projectColumns = (): ColumnDef<ProjectRow>[] => [
  {
    id: "thumbnail",
    header: "",
    enableSorting: false,
    meta: { variant: "narrow" },
    cell: ({ row: { original: rowData } }) => (
      <EnlargeableThumbnail project={rowData.project} />
    ),
  },
  {
    id: "name",
    header: t`common.name`,
    accessorFn: (rowData) => rowData.project.name,
    meta: { customTd: true },
    cell: ({ row: { original: rowData } }) => (
      <CustomCellLink to={projectUrls.index(rowData.project.id)}>
        <S.ProjectName>{rowData.project.name}</S.ProjectName>
      </CustomCellLink>
    ),
  },
  {
    id: "modified",
    header: t`common.modified-label-only`,
    accessorFn: (rowData) => rowData.project.updatedAt,
    cell: ({ row: { original: rowData } }) => (
      <RelativeDateTime value={rowData.project.updatedAt} />
    ),
  },
  {
    id: "printed",
    header: t`common.printed`,
    accessorFn: (rowData) => rowData.lastAttempt?.startedAt ?? "",
    cell: ({ row: { original: rowData } }) => (
      <RelativeDateTime
        value={rowData.lastAttempt?.startedAt}
        defaultValue={t`common.not-printed`}
      />
    ),
  },
  {
    id: "materials",
    header: t`common.materials`,
    enableSorting: false,
    cell: ({ row: { original: rowData } }) => (
      <MaterialsList materials={rowData.materials} />
    ),
  },
  {
    id: "owner",
    header: t`common.owner`,
    accessorFn: (rowData) => rowData.owner?.name ?? "",
    cell: ({ row: { original: rowData } }) => {
      const owner = rowData.owner;

      if (!owner) {
        return null;
      }

      return (
        <S.OwnerCell>
          <UserAvatar resource={owner} size="small" />
          <span>{owner.name}</span>
        </S.OwnerCell>
      );
    },
  },
];

export const ProjectsTable = ({
  limit = 200,
  showArchived = false,
}: {
  limit?: number;
  showArchived?: boolean;
}) => {
  const { allProjects, active, archived, attemptsByProject, isLoading } =
    useProjectsListing({ limit });
  const { data: allMaterials = [] } = useMaterials();
  const { data: allUsers = [] } = useUsers();
  const [_location, setLocation] = useLocation();

  const materialsById = indexById(allMaterials);
  const usersById = indexById(allUsers);

  const toRows = (projects: Project[]): ProjectRow[] =>
    projects.map((project) => {
      const attempts = attemptsByProject.get(project.id) ?? [];

      return {
        project,
        owner: usersById[project.ownerId],
        lastAttempt: attempts.toSorted(sortAttempts)[0],
        materials: projectMaterials(project, materialsById),
      };
    });

  const columns = projectColumns();

  const getRowProps = (row: { original: ProjectRow }) => ({
    onClick: (event: MouseEvent<HTMLTableRowElement>) => {
      // The name cell is already a link. Leave its clicks alone so browser
      // features like "open in new tab" keep working and we don't navigate twice.
      if (event.target instanceof Element && event.target.closest("a")) {
        return;
      }

      setLocation(projectUrls.index(row.original.project.id));
    },
  });

  const renderTable = (projects: Project[]) => (
    <Table
      data={toRows(projects)}
      columns={columns}
      loading={isLoading}
      noDataLabel={t`common.no-projects`}
      customCellCss={S.clickableCell}
      getRowProps={getRowProps}
    />
  );

  return (
    <>
      {renderTable(active)}

      {showArchived && allProjects.length > 0 && (
        <>
          <ArchivedProjectsHeader />
          {renderTable(archived)}
        </>
      )}
    </>
  );
};
